import React, { useEffect, useState } from 'react';
import {
  AccessTimeOutlined,
  AssignmentIndOutlined,
  AssignmentOutlined,
  EventAvailableOutlined,
  ForumOutlined,
  FlagOutlined,
  HowToRegOutlined,
  EditOutlined,
  PersonOutlineOutlined,
  SupervisorAccountOutlined,
  VisibilityOutlined,
} from '@material-ui/icons';
import { Avatar, IconButton, Tooltip } from '@material-ui/core';
import { formatDate } from '../../../../utils/format';
import RegistrationMissingFields from './registrationMissingFields';
import useUserProfilePhoto from './useUserProfilePhoto';

const getPlatformRegistrationLabel = (identityMethods = []) => {
  const normalizedMethods = Array.isArray(identityMethods)
    ? [...new Set(identityMethods.filter(Boolean))]
    : [];
  const hasPassword = normalizedMethods.includes('password');
  const hasGoogle = normalizedMethods.includes('google');

  if (hasPassword && hasGoogle) {
    return 'Registrado con google y con email & contraseña';
  }

  if (hasGoogle) {
    return 'Registrado con google';
  }

  if (hasPassword) {
    return 'Registrado con email y contraseña';
  }

  return 'No disponible';
};

const getRegistrationCompletionLabel = (user) => {
  if (user?.registrationUpdateRequired === true) {
    return 'Incompleto';
  }

  if (user?.registro === 0) {
    return 'Completo';
  }

  if (Number.isInteger(user?.registro) && user.registro > 0) {
    return 'Incompleto';
  }

  return 'No disponible';
};

const SessionSection = ({ user, children, onEditRole }) => {
  const role = user?.role || { name: 'Sin rol', type: 'none' };
  const hasRegistration = ['mentor', 'mentee'].includes(role.type);
  const photoProfile = useUserProfilePhoto(hasRegistration ? user : null);
  const registrationCompletionLabel = getRegistrationCompletionLabel(user);
  const isRegistrationIncomplete =
    hasRegistration && registrationCompletionLabel === 'Incompleto';
  const [missingFieldsExpanded, setMissingFieldsExpanded] = useState(false);
  const missingFieldsId = `registration-missing-fields-${user?.id || 'user'}`;

  useEffect(() => {
    setMissingFieldsExpanded(false);
  }, [user?.id, role.type, registrationCompletionLabel]);

  const sessions = Array.isArray(user?.sessions) ? user.sessions : [];
  const registrationLabel = getPlatformRegistrationLabel(user?.identityMethods);
  const platformAnnouncementName =
    user?.platformAnnouncement?.name || 'Plataforma';
  const profileRegistrationLabel = user?.preRegistry
    ? 'Postulación'
    : 'Registro Administrador';
  const stats = [
    {
      label: 'Rol',
      value: role.name,
      icon: <SupervisorAccountOutlined />,
      editable: true,
    },
    ...(hasRegistration
      ? [
          {
            label: 'Estado del registro',
            value: registrationCompletionLabel,
            icon: <AssignmentOutlined />,
            registration: true,
          },
        ]
      : []),
    {
      label: 'Tipo de registro en plataforma',
      value: registrationLabel,
      icon: <HowToRegOutlined />,
      fullWidth: true,
      accent: true,
    },
    {
      label: 'Convocatoria activa',
      value: platformAnnouncementName,
      icon: <FlagOutlined />,
      fullWidth: true,
    },
    ...(role.type === 'mentor'
      ? [
          {
            label: 'Tipo de registro',
            value: profileRegistrationLabel,
            icon: <AssignmentIndOutlined />,
          },
        ]
      : []),
    {
      label: 'Reuniones realizadas',
      value: String(user?.totalSessions ?? 0),
      icon: <ForumOutlined />,
    },
    {
      label: 'Fecha de enrolamiento',
      value: formatDate(user?.createdAt),
      icon: <EventAvailableOutlined />,
    },
    {
      label: 'Última conexión',
      value: sessions.length
        ? formatDate(sessions[0].createdAt, 'DD/MM/YYYY HH:mm')
        : 'Nunca',
      icon: <AccessTimeOutlined />,
    },
  ];

  return (
    <section className="personal-info session-overview">
      <div className="session-overview__header">
        <div>
          <span className="session-overview__eyebrow">Resumen del usuario</span>
          <h3 className="session-overview__title">Estado de la cuenta</h3>
        </div>
        <span className="session-overview__pill">
          {role.name || 'Sin rol'}
        </span>
      </div>
      {hasRegistration && (
        <div className="session-overview__profile">
          <Avatar
            className="session-overview__avatar"
            src={photoProfile || undefined}
            alt={`Foto de perfil de ${user?.name || 'usuario'}`}
          >
            <PersonOutlineOutlined titleAccess="Sin foto de perfil disponible" />
          </Avatar>
          <div className="session-overview__profile-copy">
            <span className="session-overview__label">Foto de perfil</span>
            <strong className="session-overview__profile-name">
              {user?.name || 'Usuario'}
            </strong>
          </div>
        </div>
      )}
      <div className="session-overview__grid">
        {stats.map((stat) => (
          <React.Fragment key={stat.label}>
            <article
              className={`session-overview__card${
                stat.fullWidth ? ' session-overview__card--full' : ''
              }${stat.accent ? ' session-overview__card--accent' : ''}`}
            >
              <div className="session-overview__icon">{stat.icon}</div>
              <div className="session-overview__content">
                <span className="session-overview__label">{stat.label}</span>
                <strong className="session-overview__value">{stat.value}</strong>
              </div>
              {stat.editable && onEditRole && (
                <Tooltip title="Cambiar rol">
                  <IconButton
                    type="button"
                    className="session-overview__role-edit"
                    aria-label={`Cambiar rol de ${role.name || 'usuario'}`}
                    aria-haspopup="dialog"
                    aria-controls="change-user-role-dialog"
                    onClick={onEditRole}
                  >
                    <EditOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {stat.registration && isRegistrationIncomplete && (
                <Tooltip title="Ver campos faltantes">
                  <IconButton
                    type="button"
                    className="session-overview__registration-view"
                    aria-label="Ver campos faltantes"
                    aria-controls={missingFieldsId}
                    aria-expanded={missingFieldsExpanded}
                    onClick={() => setMissingFieldsExpanded(true)}
                  >
                    <VisibilityOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </article>
            {stat.registration && isRegistrationIncomplete && (
              <RegistrationMissingFields
                key={`${user?.id || 'user'}-${role.type}`}
                id={missingFieldsId}
                expanded={missingFieldsExpanded}
                onChange={(event, expanded) => setMissingFieldsExpanded(expanded)}
                missingFields={user?.registrationMissingFields}
                roleType={role.type}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      {children}
    </section>
  );
};
export default SessionSection;
